import * as R from 'ramda';
import React, { useCallback } from 'react';
// features
import { AwardedLaneImport } from '../../new-import';
import {
  IMPORT_TYPE_CARRIER_AWARDED_LINE,
  IMPORT_TYPE_CUSTOMER_AWARDED_LINE,
} from '../../new-import/constants';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import { getFreightClassOptions, getGroupedByCountryStateOptionsByConfig } from '../../../helpers/options';
// ui
import { MainActionButton } from '../../../ui';
// utilities
import endpointsMap from '../../../utilities/endpoints';
// feature carrier-profile
import { removeEntityRequest } from '../actions';
import AwarderLaneForm from '../forms/awarded-lane';
//////////////////////////////////////////////////

const AdditionalReportTitleComponent = (props) => {
  const {
    openModal,
    primaryObjectGuid,
    getItemListRequest,
    awardedLaneImportType,
    handleResetListAndPagination,
  } = props;

  const handleOpenAwardedLaneImport = useCallback(() => {
    const component = (
      <AwardedLaneImport
        fileType={awardedLaneImportType}
        contractGuid={primaryObjectGuid}
        getCarrierContractRatesRequest={getItemListRequest}
        resetCarrierContractListAndPagination={handleResetListAndPagination}
      />
    );

    const modal = {
      p: 15,
      component,
      options: {
        width: '90vw',
        height: '90vh',
        movable: false,
      },
    };

    openModal(modal);
  }, [
    openModal,
    primaryObjectGuid,
    getItemListRequest,
    awardedLaneImportType,
    handleResetListAndPagination,
  ]);

  return (
    <MainActionButton
      ml={20}
      mb='3px'
      height={30}
      onClick={handleOpenAwardedLaneImport}
    >
      {G.getWindowLocale('titles:import', 'Import')}
    </MainActionButton>
  );
};

const getAddressPointFieldsByType = (prefix) => {
  const localePrefix = G.ifElse(
    R.equals(prefix, GC.FIELD_ORIGIN),
    'titles:origin',
    'titles:destination',
  );

  const getLocaleArr = (key) => [localePrefix, key];

  return {
    [`${prefix}.${GC.FIELD_GEO_FENCING_ZONE_CITIES}`]: {
      width: 200,
      collection: true,
      name: getLocaleArr('titles:cities'),
    },
    [`${prefix}.${GC.FIELD_GEO_FENCING_ZONE_STATES}`]: {
      width: 200,
      uiType: 'state',
      collection: true,
      name: getLocaleArr('titles:states'),
      filterType: 'selectMultipleCreatable',
      options: getGroupedByCountryStateOptionsByConfig,
    },
    [`${prefix}.${GC.FIELD_GEO_FENCING_ZONE_COUNTRIES}`]: {
      width: 200,
      collection: true,
      name: getLocaleArr('titles:countries'),
    },
    [`${prefix}.${GC.FIELD_GEO_FENCING_ZONE_ZIP_CODES}`]: {
      width: 200,
      collection: true,
      name: getLocaleArr('titles:zip-codes'),
    },
    [`${prefix}.${GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_FROM}`]: {
      name: getLocaleArr('titles:zip-from'),
    },
    [`${prefix}.${GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_TO}`]: {
      name: getLocaleArr('titles:zip-to'),
    },
  };
};

const getFreightClassLabel = (value) => R.compose(
  R.propOr(value, 'label'),
  R.find(R.propEq(value, GC.FIELD_VALUE)),
)(getFreightClassOptions());

const getFreightClassOverrideLabel = (override) => G.createStringFromArray([
  getFreightClassLabel(R.prop(GC.FIELD_FROM_CLASS, override)),
  '-',
  getFreightClassLabel(R.prop(GC.FIELD_TO_CLASS, override)),
  '=>',
  getFreightClassLabel(R.prop(GC.FIELD_OVERRIDE_CLASS, override)),
], ' ');

const fields = {
  [GC.FIELD_NAME]: {
    width: 200,
    name: 'titles:name',
  },
  [GC.FIELD_CHARGE_MIN_RATE]: {
    width: 200,
    filterType: 'number',
    name: 'titles:min-rate',
  },
  [GC.FIELD_EFFECTIVE_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:effective-date',
  },
  [GC.FIELD_EXPIRATION_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:expiration-date',
  },
  [GC.FIELD_FREIGHT_CLASS_OVERRIDES]: {
    width: 250,
    name: 'titles:freight-class-overrides',
    customComponent: ({ data }) => G.createStringFromArray(
      R.map(getFreightClassOverrideLabel, R.propOr([], GC.FIELD_FREIGHT_CLASS_OVERRIDES, data)),
      ', ',
    ),
  },
  ...getAddressPointFieldsByType(GC.FIELD_ORIGIN),
  ...getAddressPointFieldsByType(GC.FIELD_DESTINATION),
  [GC.FIELD_CREATED_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:created-date',
  },
  [GC.FIELD_CREATED_BY]: {
    width: 200,
    name: 'titles:created-by',
  },
  [GC.FIELD_LAST_MODIFIED_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:updated-date',
  },
  [GC.FIELD_LAST_MODIFIED_BY]: {
    width: 150,
    name: 'titles:updated-by',
  },
};

const makeRequestPayload = ({ primaryObjectGuid, primaryObjectBranchGuid }) => ({
  [GC.CURRENT_BRANCH]: primaryObjectBranchGuid,
  params: { [GC.FIELD_CONTRACT_GUID]: primaryObjectGuid },
});

const placeholder = { text: 'Choose', key: 'titles:choose' };

const filterProps = R.mapObjIndexed((
  { name, options, collection, filterType, type = 'string' },
  value,
) => ({
  value,
  options,
  collection,
  placeholder,
  type: R.or(filterType, type),
  [GC.FIELD_NAME]: G.ifElse(
    G.isArray(name),
    `${G.getWindowLocale(name[0])}: ${G.getWindowLocale(name[1])}`,
    G.getWindowLocale(name),
  ),
}), fields);

const carrierContractAwardedLaneSettings = {
  fields,
  filterProps,
  isReport: true,
  useGetListRequest: true,
  useSearchableColumns: true,
  withResizableColumns: true,
  useNewTitleFilterInputs: true,
  tablePropsMemoDeps: ['isSmc3'],
  getItemListRequestMethod: 'post',
  groupName: 'carrierContract.awardedLane',
  primaryObjectGuidKey: GC.FIELD_CONTRACT_GUID,
  omitFormFields: GC.GROUPED_FIELDS.SYSTEM_OMIT_ARR,
  reportType: GC.CARRIER_CONTRACT_AWARDED_LINE_REPORT,
  additionalTableSettings: { searchableTitles: true },
  itemTitleArr: ['titles:awarded-lane', 'Awarded Lane'],
  awardedLaneImportType: IMPORT_TYPE_CARRIER_AWARDED_LINE,
  formGroupTitleArr: ['titles:contract-awarded-lanes', 'Contract Awarded Lanes'],
  endpoints: {
    list: 'carrierContractAwardedLineList',
    createOrUpdate: 'carrierContractAwardedLine',
  },
  // components
  CustomForm: AwarderLaneForm,
  AdditionalReportTitleComponent,
  // helpers
  makeRequestPayload,
  makeInitialValues: (initialValues, { carrierGuid, primaryObjectGuid }) => ({
    ...initialValues,
    carrierGuid,
    [GC.FIELD_CONTRACT_GUID]: primaryObjectGuid,
  }),
  customRemoveHandler: ({ dispatch, groupName }, { guid }) => {
    const map = {
      'carrierContract.awardedLane': 'carrierContractAwardedLine',
      'customerContract.awardedLane': 'customerContractAwardedLine',
    };

    const endpointName = R.prop(groupName, map);
    const endpoint = R.prop(endpointName, endpointsMap);
    const requestOptions = { data: R.of(Array, guid) };

    dispatch(removeEntityRequest({ guid, isReport: true, method: 'delete', endpoint, groupName, requestOptions }));
  },
};

const customerContractAwardedLaneSettings = {
  ...carrierContractAwardedLaneSettings,
  groupName: 'customerContract.awardedLane',
  reportType: GC.CUSTOMER_CONTRACT_AWARDED_LINE_REPORT,
  awardedLaneImportType: IMPORT_TYPE_CUSTOMER_AWARDED_LINE,
  endpoints: {
    list: 'customerContractAwardedLineList',
    createOrUpdate: 'customerContractAwardedLine',
  },
};

export {
  filterProps,
  carrierContractAwardedLaneSettings,
  customerContractAwardedLaneSettings,
};
